import React, { useContext, useCallback, useEffect, useState } from 'react'
import {
  AddIcon,
  Button,
  CurrencyInput,
  SubtractIcon,
  Col,
  Row,
} from '@paypalcorp/pp-react'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { interpolate } from 'ppbl/utils/string-helper'
import { toCommaSeparatedNumber } from '../__utils__'
import { CleverContext } from './cleverContext'
import {
  composeValidatorWithValue,
  minCurrencyVal,
  maxCurrencyVal,
} from 'ppbl/utils/validation-helper'
import { LOAN_INPUT_STEPPER_DELTA } from 'ppbl/utils/cleverConstants'
import { isApplicationAutomationEligible } from 'ppbl/utils/clever-utils'
import './styles.css'
import InputSlider from './Slider'

/**
 * This is a functional component that takes input of desired loan amount
 * it also validates input against min and max pffer amount limiy
 * @returns {JSX.Element} - A JSX element
 */
const CleverOfferInput: React.FC = () => {
  const {
    loanAmount,
    updateLoanAmount,
    offerMaxAmount,
    offerMinAmount,
    disableContinue,
    offerValues,
    application,
  } = useContext(CleverContext)
  const [inputError, setInputError] = useState(false)
  const offerInputHelperText = interpolate(
    getLocalizedText(
      'cleverCalculator-page.cleverOfferInput.offerInputHelperText',
    ),
    {
      minAmount: `$${toCommaSeparatedNumber(offerMinAmount)}`,
      maxAmount: `$${toCommaSeparatedNumber(offerMaxAmount)}`,
    },
  )

  const deltaValue = LOAN_INPUT_STEPPER_DELTA

  const isAutomationEligible = isApplicationAutomationEligible(
      application?.tags,
    )

  const determineMinusState = useCallback(() => {
    return Number(loanAmount) <= Number(offerMinAmount)
  }, [loanAmount, offerMinAmount])

  const determinePlusState = useCallback(() => {
    return Number(loanAmount) >= Number(offerMaxAmount)
  }, [loanAmount, offerMaxAmount])

  const [isMinusDisabled, setMinusDiable] = useState(determineMinusState())
  const [isPlusDisabled, setPlusDiable] = useState(determinePlusState())

  // checks the validity of input box
  const offerInputValidation = (input) => {
    return composeValidatorWithValue({
      label: getLocalizedText(
        'cleverCalculator-page.cleverOfferInput.offerInputLabel',
      ),
      validators: [
        minCurrencyVal(offerMinAmount),
        maxCurrencyVal(offerMaxAmount),
      ],
      value: input,
    })
  }

  const isValidInput = (inputValue) => {
    if (
      Number(inputValue) >= Number(offerMinAmount) &&
      Number(inputValue) <= Number(offerMaxAmount)
    ) {
      return true
    }
    return false
  }

  const handleDecrement = () => {
    if (!isMinusDisabled) {
      const validDecrement = () => {
        if (isValidInput(Number(loanAmount) - deltaValue)) {
          return Number(loanAmount) - deltaValue
        }
        if (isValidInput(loanAmount)) {
          return offerMinAmount
        }
        return offerMaxAmount
      }
      updateLoanAmount(String(validDecrement()))
    }
  }

  const handleIncrement = () => {
    if (!isPlusDisabled) {
      const validIncrement = () => {
        if (isValidInput(Number(loanAmount) + deltaValue)) {
          return Number(loanAmount) + deltaValue
        }
        if (isValidInput(loanAmount)) {
          return offerMaxAmount
        }
        return offerMinAmount
      }
      updateLoanAmount(String(validIncrement()))
    }
  }

  useEffect(() => {
    setMinusDiable(determineMinusState())
    setPlusDiable(determinePlusState())
  }, [determineMinusState, determinePlusState, loanAmount])

  /**
   * This function is triggered on user input and validats the unmasked input
   * throws error if the value is out of bound else sets input
   */

  const handleAccept = (
    _value: string,
    unmaskedValue: string,
    _typedValue: string,
  ) => {
    const errorMessage = offerInputValidation(unmaskedValue)
    setInputError(errorMessage)
    disableContinue(errorMessage ? true : false)
    updateLoanAmount(unmaskedValue)
  }

  const handleTrackInput = (event) => {
    if (Number(loanAmount) < Number(offerMinAmount)) {
      updateLoanAmount(String(offerMinAmount))
    } else if (Number(loanAmount) > Number(offerMaxAmount)) {
      updateLoanAmount(String(offerMaxAmount))
    }
  }

  const showAutomationEligibleHighlight =
      isAutomationEligible === 'Y' &&
      offerValues?.maxAutoQualifiedAmount &&
      Number(loanAmount) <= Number(offerValues?.maxAutoQualifiedAmount)

  return (
    <div className="cleverOfferInput">
      <Row className="cleverOfferInputInnerWrapper">
        <Col>
          <Button
            type="button"
            aria-label="Decrement Loan Amount"
            className="button"
            disabled={isMinusDisabled}
            onClick={handleDecrement}
            data-testid="decrement-button"
          >
            <SubtractIcon size="sm" />
          </Button>
        </Col>
        <Col xl={8} lg={8} sm={11} xs={11} className="offerInputSliderCol">
          <CurrencyInput
            className="clever-currency-input-container"
            style={{ textAlign: 'center' }}
            name="offer-amount-input"
            aria-label={getLocalizedText(
              'cleverCalculator-page.cleverOfferInput.offerInputLabel',
            )}
            onAccept={handleAccept}
            onBlur={handleTrackInput}
            value={loanAmount}
            helperText={offerInputHelperText}
            errorText={
              inputError &&
              interpolate(
                getLocalizedText(
                  'cleverCalculator-page.cleverOfferInput.inputErrorText',
                ),
                {
                  offerInputHelperText,
                },
              )
            }
            maskOptions={{
              mask: '$ num',
              blocks: {
                num: {
                  mask: Number,
                  thousandsSeparator: ',',
                  padFractionalZeros: false,
                },
              },
              lazy: false,
            }}
          />
          <InputSlider 
            min={offerMinAmount}
            max={offerMaxAmount}
            value={loanAmount}
            prefix={'$'}
            stepper={LOAN_INPUT_STEPPER_DELTA}
            onChange={(value) => updateLoanAmount(String(value))}
          />
          <Row className="autoQualifiedBarContainer">
            {showAutomationEligibleHighlight &&
              <Row className="autoQualifiedBarWrapper">
                <Col sm={1} className="autoQualifiedBar" />
                <Col className="autoQualifiedBarText">
                  {getLocalizedText("cleverCalculator-page.cleverOfferInput.autoQualifiedLoanShortLabel")} (${toCommaSeparatedNumber(offerValues?.maxAutoQualifiedAmount)})
                </Col>
              </Row>
            }
          </Row>
          <Row className="slider-range-row">
            <Col className="slider-range-col">${toCommaSeparatedNumber(offerMinAmount)}</Col>
            <Col className="slider-range-col">${toCommaSeparatedNumber(offerMaxAmount)}</Col>
          </Row>
        </Col>
        <Col>
          <Button
            type="button"
            aria-label="Increment Loan Amount"
            className="button"
            disabled={isPlusDisabled}
            onClick={handleIncrement}
            data-testid="increment-button"
          >
            <AddIcon size="sm" />
          </Button>
        </Col>
      </Row>
    </div>
  )
}

export default CleverOfferInput
